import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Building2, Trash2, Edit, AlertCircle, TrendingUp, TrendingDown, FileText, ArrowRightLeft, Calendar, Hash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Obtener fecha de Guatemala
const getGuatemalaDate = () => {
  const now = new Date();
  const guatemalaTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Guatemala' }));
  return guatemalaTime.toISOString().split('T')[0];
};

const Banks = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferHistoryOpen, setTransferHistoryOpen] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [editingBank, setEditingBank] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_number: '',
    account_type: 'Ahorro',
    initial_balance: 0,
    low_balance_alert: 0
  });
  const [transferData, setTransferData] = useState({
    source_bank_id: '',
    destination_bank_id: '',
    amount: '',
    reference_number: '',
    description: '',
    transfer_date: getGuatemalaDate()
  });

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data);
    } catch (error) {
      toast.error('Error al cargar bancos');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransfers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/banks/transfers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransfers(response.data);
    } catch (error) {
      toast.error('Error al cargar transferencias');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    if (!transferData.source_bank_id || !transferData.destination_bank_id) {
      toast.error('Selecciona banco origen y destino');
      return;
    }
    
    if (!transferData.amount || parseFloat(transferData.amount) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    // Verificar saldo disponible
    const sourceBank = banks.find(b => b.id === transferData.source_bank_id);
    if (sourceBank && parseFloat(transferData.amount) > sourceBank.current_balance) {
      toast.error(`Saldo insuficiente. Disponible: Q${sourceBank.current_balance.toLocaleString('es-GT', {minimumFractionDigits: 2})}`);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/banks/transfer`, {
        ...transferData,
        amount: parseFloat(transferData.amount),
        transfer_date: transferData.transfer_date + 'T12:00:00'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Transferencia realizada con éxito');
      setTransferDialogOpen(false);
      resetTransferForm();
      fetchBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al realizar transferencia');
    }
  };

  const resetTransferForm = () => {
    setTransferData({
      source_bank_id: '',
      destination_bank_id: '',
      amount: '',
      reference_number: '',
      description: '',
      transfer_date: getGuatemalaDate()
    });
  };

  const getSourceBankBalance = () => {
    if (!transferData.source_bank_id) return null;
    const bank = banks.find(b => b.id === transferData.source_bank_id);
    return bank ? bank.current_balance : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingBank) {
        await axios.put(`${API}/banks/${editingBank.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banco actualizado correctamente');
      } else {
        await axios.post(`${API}/banks`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banco creado correctamente');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchBanks();
    } catch (error) {
      toast.error('Error al guardar banco');
    }
  };

  const handleEdit = (bank) => {
    setEditingBank(bank);
    setFormData({
      bank_name: bank.bank_name,
      account_number: bank.account_number,
      account_type: bank.account_type,
      initial_balance: bank.initial_balance,
      low_balance_alert: bank.low_balance_alert
    });
    setDialogOpen(true);
  };

  const handleDelete = async (bankId) => {
    if (!window.confirm('¿Estás seguro de desactivar este banco?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/banks/${bankId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banco desactivado correctamente');
      fetchBanks();
    } catch (error) {
      toast.error('Error al desactivar banco');
    }
  };

  const resetForm = () => {
    setFormData({
      bank_name: '',
      account_number: '',
      account_type: 'Ahorro',
      initial_balance: 0,
      low_balance_alert: 0
    });
    setEditingBank(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2
    }).format(value);
  };

  const getTotalBalance = () => {
    return banks.reduce((sum, bank) => sum + bank.current_balance, 0);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header con resumen */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Bancos</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Gestiona tus cuentas bancarias</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Botón Transferir */}
          <Button 
            variant="outline" 
            onClick={() => setTransferDialogOpen(true)}
            disabled={banks.length < 2}
            className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400"
            data-testid="transfer-button"
          >
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Transferir entre Bancos
          </Button>

          {/* Botón Historial */}
          <Button 
            variant="outline" 
            onClick={() => {
              fetchTransfers();
              setTransferHistoryOpen(true);
            }}
            className="border-slate-300 dark:border-slate-600"
            data-testid="transfer-history-button"
          >
            <FileText className="h-4 w-4 mr-2" />
            Historial
          </Button>

          {/* Botón Nuevo Banco */}
          <Button onClick={() => { setEditingBank(null); setDialogOpen(true); }} data-testid="new-bank-button">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Banco
          </Button>
        </div>
      </div>

      {/* Dialog Nuevo/Editar Banco */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingBank ? 'Editar Banco' : 'Nuevo Banco'}
            </DialogTitle>
          </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del Banco</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="Ej: Banco Industrial"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Número de Cuenta</Label>
                <Input
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  placeholder="Ej: 1234567890"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Tipo de Cuenta</Label>
                <Select
                  value={formData.account_type}
                  onValueChange={(value) => setFormData({ ...formData, account_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ahorro">Ahorro</SelectItem>
                    <SelectItem value="Monetaria">Monetaria</SelectItem>
                    <SelectItem value="Corriente">Corriente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {!editingBank && (
                <div className="space-y-2">
                  <Label>Saldo Inicial</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.initial_balance}
                    onChange={(e) => setFormData({ ...formData, initial_balance: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-slate-500">
                    El saldo con el que empieza esta cuenta
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Alerta de Saldo Bajo</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.low_balance_alert}
                  onChange={(e) => setFormData({ ...formData, low_balance_alert: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
                <p className="text-xs text-slate-500">
                  Recibirás una alerta cuando el saldo sea menor a este monto
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">
                  {editingBank ? 'Actualizar' : 'Crear'} Banco
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      {/* Resumen Total */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Saldo Total en Bancos</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">
                {formatCurrency(getTotalBalance())}
              </p>
            </div>
            <Building2 className="h-12 w-12 text-blue-600 opacity-50" />
          </div>
          <div className="mt-4 flex gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-blue-600"></div>
              <span>{banks.length} {banks.length === 1 ? 'banco' : 'bancos'} activos</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Bancos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-12 text-slate-600">
            Cargando bancos...
          </div>
        ) : banks.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No hay bancos registrados</p>
            <p className="text-sm text-slate-500 mt-1">
              Crea tu primer banco para empezar a gestionar tus cuentas
            </p>
          </div>
        ) : (
          banks.map((bank) => (
            <Card key={bank.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{bank.bank_name}</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        {bank.account_type} • {bank.account_number}
                      </p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-600">Saldo Actual</p>
                    <p className={`text-2xl font-bold ${
                      bank.current_balance < 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatCurrency(bank.current_balance)}
                    </p>
                  </div>

                  {bank.low_balance_alert > 0 && bank.current_balance < bank.low_balance_alert && (
                    <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>Saldo bajo</span>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleEdit(bank)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(bank.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Dialog de Transferencia */}
      <Dialog open={transferDialogOpen} onOpenChange={(open) => {
        setTransferDialogOpen(open);
        if (!open) resetTransferForm();
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-blue-600" />
              Transferir entre Bancos
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleTransfer} className="space-y-4">
            {/* Banco Origen */}
            <div className="space-y-2">
              <Label>Banco Origen *</Label>
              <select
                value={transferData.source_bank_id}
                onChange={(e) => setTransferData({ ...transferData, source_bank_id: e.target.value })}
                className="w-full p-2 border rounded-md bg-white dark:bg-slate-800 dark:border-slate-600"
                required
                data-testid="source-bank-select"
              >
                <option value="">Seleccionar banco origen...</option>
                {banks.filter(b => b.id !== transferData.destination_bank_id).map(bank => (
                  <option key={bank.id} value={bank.id}>
                    {bank.bank_name} ({bank.account_number}) - Q{bank.current_balance.toLocaleString('es-GT', {minimumFractionDigits: 2})}
                  </option>
                ))}
              </select>
              {getSourceBankBalance() !== null && (
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                  Saldo disponible: {formatCurrency(getSourceBankBalance())}
                </p>
              )}
            </div>

            {/* Banco Destino */}
            <div className="space-y-2">
              <Label>Banco Destino *</Label>
              <select
                value={transferData.destination_bank_id}
                onChange={(e) => setTransferData({ ...transferData, destination_bank_id: e.target.value })}
                className="w-full p-2 border rounded-md bg-white dark:bg-slate-800 dark:border-slate-600"
                required
                data-testid="destination-bank-select"
              >
                <option value="">Seleccionar banco destino...</option>
                {banks.filter(b => b.id !== transferData.source_bank_id).map(bank => (
                  <option key={bank.id} value={bank.id}>
                    {bank.bank_name} ({bank.account_number})
                  </option>
                ))}
              </select>
            </div>

            {/* Monto */}
            <div className="space-y-2">
              <Label>Monto a Transferir (Q) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={transferData.amount}
                onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                placeholder="0.00"
                required
                data-testid="transfer-amount-input"
              />
              {transferData.amount && getSourceBankBalance() !== null && parseFloat(transferData.amount) > getSourceBankBalance() && (
                <p className="text-sm text-red-600 font-medium">
                  Saldo insuficiente para esta transferencia
                </p>
              )}
            </div>

            {/* Fecha */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Fecha de Transferencia
              </Label>
              <Input
                type="date"
                value={transferData.transfer_date}
                onChange={(e) => setTransferData({ ...transferData, transfer_date: e.target.value })}
                data-testid="transfer-date-input"
              />
            </div>

            {/* Número de Referencia */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Hash className="h-4 w-4" />
                Número de Referencia/Transferencia
              </Label>
              <Input
                value={transferData.reference_number}
                onChange={(e) => setTransferData({ ...transferData, reference_number: e.target.value })}
                placeholder="Ej: TRF-12345"
                data-testid="transfer-reference-input"
              />
            </div>

            {/* Concepto */}
            <div className="space-y-2">
              <Label>Concepto / Descripción</Label>
              <Input
                value={transferData.description}
                onChange={(e) => setTransferData({ ...transferData, description: e.target.value })}
                placeholder="Ej: Traspaso para nómina"
                data-testid="transfer-description-input"
              />
            </div>

            {/* Resumen */}
            {transferData.source_bank_id && transferData.destination_bank_id && transferData.amount && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Resumen de Transferencia</h4>
                <div className="text-sm space-y-1 text-blue-700 dark:text-blue-400">
                  <p><strong>De:</strong> {banks.find(b => b.id === transferData.source_bank_id)?.bank_name}</p>
                  <p><strong>A:</strong> {banks.find(b => b.id === transferData.destination_bank_id)?.bank_name}</p>
                  <p><strong>Monto:</strong> {formatCurrency(parseFloat(transferData.amount) || 0)}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={!transferData.source_bank_id || !transferData.destination_bank_id || !transferData.amount || (getSourceBankBalance() !== null && parseFloat(transferData.amount) > getSourceBankBalance())}
                data-testid="confirm-transfer-button"
              >
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                Confirmar Transferencia
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Historial de Transferencias */}
      <Dialog open={transferHistoryOpen} onOpenChange={setTransferHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Historial de Transferencias
            </DialogTitle>
          </DialogHeader>
          
          {transfers.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay transferencias registradas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 dark:bg-slate-800">
                  <tr>
                    <th className="text-left p-3 font-semibold">Fecha</th>
                    <th className="text-left p-3 font-semibold">Origen</th>
                    <th className="text-left p-3 font-semibold">Destino</th>
                    <th className="text-right p-3 font-semibold">Monto</th>
                    <th className="text-left p-3 font-semibold">Referencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {transfers.map(transfer => (
                    <tr key={transfer.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="p-3 text-slate-600 dark:text-slate-400">
                        {new Date(transfer.transfer_date).toLocaleDateString('es-GT')}
                      </td>
                      <td className="p-3">
                        <span className="text-red-600 dark:text-red-400">
                          {transfer.source_bank_name}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="text-green-600 dark:text-green-400">
                          {transfer.destination_bank_name}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono font-semibold">
                        {formatCurrency(transfer.amount)}
                      </td>
                      <td className="p-3 text-slate-500 dark:text-slate-400">
                        {transfer.reference_number || '-'}
                        {transfer.description && (
                          <span className="block text-xs text-slate-400">{transfer.description}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Banks;
